import * as Yup from 'yup';
import type { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';

export interface SendPudlReceiptProps {
  legId: number;
  jobId: number;
  csnId: number;
  custRefNo?: string;
  bookingRefNo?: string;
  cntrId?: string;
  driverCellPhone?: string;
  driverEmail?: string;
  isHazmat?: boolean;
  isImport?: boolean;
  isExport?: boolean;
  /** When more than one leg is given, every leg's receipt is sent, each to its own driver unless an address is entered. */
  legs?: DispatchLeg[];
  /** Multiple legs only: the legs belong to different drivers, so the receipts can only be printed, not emailed or texted. */
  printOnly?: boolean;
}

export interface SendPudlReceiptForm {
  sendEmail: boolean;
  sendText: boolean;
  incAllLegs: boolean;
  incCustInfo: boolean;
  cellPhone: string;
  emailAddr: string;
  emailSubject: string;
  emailBody: string;
  textBody: string;
  /** Multiple legs only: address(es) that receive one email with every receipt. */
  copyEmailAddr?: string;
}

export const initialFormValues = {
  sendEmail: false,
  sendText: false,
  incAllLegs: false,
  incCustInfo: false,
  cellPhone: '',
  emailAddr: '',
  emailSubject: '',
  emailBody: '',
  textBody: ''
};

/** With multiple legs the addresses and phone number are optional, because each leg's receipt goes to its own driver. */
export function getSendPudlReceiptValidationSchema(isBatch: boolean) {
  return Yup.object<SendPudlReceiptForm>().shape(
    {
      sendEmail: Yup.bool(),

      sendText: Yup.bool().when('sendEmail', {
        is: (val: boolean) => !val,
        then: (me) => me.oneOf([true], 'Send Option is required')
      }),

      emailAddr: isBatch
        ? Yup.string().test('emailValidation', 'Please enter one or more valid Email Addresses', isValidEmailList)
        : Yup.string().when('sendEmail', {
            is: (val: boolean) => val,
            then: (me) =>
              me
                .required('Email Address is required to send PDF')
                .test('emailValidation', 'Please enter one or more valid Email Addresses', (value) => {
                  if (!value) return true;
                  const emails = value.split(',');
                  return emails.every((email) =>
                    Yup.string()
                      .email()
                      .matches(/^[^@]+@[^@]+\.[^@]+$/)
                      .isValidSync(email)
                  );
                })
          }),

      copyEmailAddr: Yup.string().test('emailValidation', 'Please enter one or more valid Email Addresses', isValidEmailList),

      cellPhone: isBatch
        ? Yup.string().test('phoneValidation', '10-digit Phone Number Required', (value) => !value || value.replaceAll(/\D/g, '').length >= 10)
        : Yup.string().when('sendText', {
            is: (val: boolean) => val,
            then: (me) => me.required('Phone Number is Required to Text PDF').min(10, '10-digit Phone Number Required')
          }),

      // With multiple legs the subject and message only apply to the optional copy email.
      emailSubject: Yup.string().when(['sendEmail', 'copyEmailAddr'], {
        is: (sendEmail: boolean, copyEmailAddr?: string) => sendEmail && (!isBatch || Boolean(copyEmailAddr)),
        then: (me) => me.required('Subject is required for Email')
      }),

      emailBody: Yup.string().when(['sendEmail', 'copyEmailAddr'], {
        is: (sendEmail: boolean, copyEmailAddr?: string) => sendEmail && (!isBatch || Boolean(copyEmailAddr)),
        then: (me) => me.required('Message is required for Email')
      }),

      // With multiple legs a blank message lets each leg's generated message be sent.
      textBody: Yup.string().when('sendText', {
        is: (val: boolean) => val && !isBatch,
        then: (me) => me.required('Message is required for Text')
      })
    },
    [['sendEmail', 'sendText']]
  );
}

/** Splits a comma/semicolon separated email list into trimmed, lower-cased, unique addresses. */
export function splitEmailList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[,;]/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

/** True when every address in a comma/semicolon separated email list is valid. */
function isValidEmailList(value?: string) {
  if (!value) return true;
  return splitEmailList(value).every((email) =>
    Yup.string()
      .email()
      .matches(/^[^@]+@[^@]+\.[^@]+$/)
      .isValidSync(email)
  );
}

/** Formats a leg the way the dispatch board identifies orders, e.g. "4679864/001". */
export function formatOrderNo(leg: DispatchLeg) {
  return `${String(leg.jobId)}/${leg.csnNo.toString().padStart(3, '0')}`;
}

/** Maps a dispatch board leg to the props of the Send Pickup/Delivery Receipt modal. */
export function toSendPudlReceiptProps(leg: DispatchLeg): SendPudlReceiptProps {
  return {
    legId: leg.legId,
    jobId: leg.jobId,
    csnId: leg.csnId,
    custRefNo: leg.csnCustRefNo,
    bookingRefNo: leg.bookingRefNo,
    cntrId: leg.cntrId,
    driverCellPhone: leg.drvCellPhone,
    driverEmail: leg.drvEmailAddr,
    isHazmat: leg.reqHazmatCert,
    isImport: leg.jobCategoryTransportationType === 'Import',
    isExport: leg.jobCategoryTransportationType === 'Export'
  };
}
